import { useState } from 'react';
import { Loader2, Save, Globe, ExternalLink, Copy, CheckCircle2, AlertCircle, Facebook, Instagram, Linkedin, Music2, Youtube } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

export default function MemberProfile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    whatsapp: user?.whatsapp || '',
    city: user?.city || '',
    company: user?.company || '',
    specialty: user?.specialty || '',
    bio: user?.bio || '',
    social_facebook: user?.social_facebook || '',
    social_instagram: user?.social_instagram || '',
    social_linkedin: user?.social_linkedin || '',
    social_tiktok: user?.social_tiktok || '',
    social_youtube: user?.social_youtube || '',
    social_website: user?.social_website || '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch('/me/profile', form);
      setUser(data);
      toast.success('Perfil actualizado.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo actualizar el perfil.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="member-profile-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Mi cuenta</p>
        <h1 className="mt-1 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">Mi perfil</h1>
      </div>

      <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-cnib-blue font-heading text-2xl font-extrabold text-white">
          {user?.name?.charAt(0)}
        </span>
        <div>
          <p className="font-heading text-lg font-bold text-slate-900">{user?.name}</p>
          <p className="text-sm text-slate-500">{user?.email}</p>
          <span className="mt-1 inline-block rounded-full bg-orange-500/10 px-3 py-0.5 text-xs font-bold text-orange-600">
            {user?.role === 'member' ? `Socio ${user?.member_number || ''}` : user?.role === 'admin' || user?.role === 'superadmin' ? 'Administración CNIB' : 'Cuenta registrada'}
          </span>
        </div>
      </div>

      <MicrositeCard user={user} isComplete={Boolean(form.city && form.whatsapp && form.company)} />

      <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6">
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-slate-700">Nombre completo</label>
          <input name="name" value={form.name} onChange={handleChange} className={inputCls} data-testid="profile-name-input" />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">WhatsApp</label>
            <input name="whatsapp" value={form.whatsapp} onChange={handleChange} className={inputCls} placeholder="+591 ..." data-testid="profile-whatsapp-input" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Ciudad</label>
            <input name="city" value={form.city} onChange={handleChange} className={inputCls} placeholder="Santa Cruz de la Sierra" data-testid="profile-city-input" />
          </div>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Empresa / Inmobiliaria</label>
            <input name="company" value={form.company} onChange={handleChange} className={inputCls} placeholder="Opcional" data-testid="profile-company-input" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Especialidad</label>
            <input name="specialty" value={form.specialty} onChange={handleChange} className={inputCls} placeholder="Ej. Vivienda residencial" data-testid="profile-specialty-input" />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-slate-700">Biografía corta</label>
          <textarea name="bio" value={form.bio} onChange={handleChange} rows={3} className={inputCls} placeholder="Contanos sobre vos y tu experiencia inmobiliaria..." data-testid="profile-bio-input" />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <p className="text-sm font-extrabold text-slate-900 flex items-center gap-2">
            <Globe className="h-4 w-4 text-cnib-blue" /> Mis redes sociales
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Aparecen en tu micrositio público, en tu credencial y en el directorio de socios.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <SocialInput name="social_instagram" icon={Instagram} label="Instagram" value={form.social_instagram} onChange={handleChange} placeholder="https://instagram.com/tu-usuario" testid="profile-social-instagram" iconClass="text-rose-500" />
            <SocialInput name="social_facebook" icon={Facebook} label="Facebook" value={form.social_facebook} onChange={handleChange} placeholder="https://facebook.com/tu-pagina" testid="profile-social-facebook" iconClass="text-blue-600" />
            <SocialInput name="social_linkedin" icon={Linkedin} label="LinkedIn" value={form.social_linkedin} onChange={handleChange} placeholder="https://linkedin.com/in/tu-perfil" testid="profile-social-linkedin" iconClass="text-sky-700" />
            <SocialInput name="social_tiktok" icon={Music2} label="TikTok" value={form.social_tiktok} onChange={handleChange} placeholder="https://tiktok.com/@tu-usuario" testid="profile-social-tiktok" iconClass="text-slate-900" />
            <SocialInput name="social_youtube" icon={Youtube} label="YouTube" value={form.social_youtube} onChange={handleChange} placeholder="https://youtube.com/@tu-canal" testid="profile-social-youtube" iconClass="text-red-600" />
            <SocialInput name="social_website" icon={Globe} label="Sitio web" value={form.social_website} onChange={handleChange} placeholder="https://tu-sitio.com" testid="profile-social-website" iconClass="text-cnib-blue" />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          data-testid="profile-save-button"
          className="flex items-center gap-2 rounded-full bg-cnib-blue px-7 py-3 font-bold text-white transition-colors hover:bg-cnib-bluehover disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar cambios
        </button>
      </form>
    </div>
  );
}

function MicrositeCard({ user, isComplete }) {
  const hasMicrosite = ['member', 'admin', 'superadmin'].includes(user?.role);
  const slug = user?.public_slug;
  const url = slug ? `${window.location.origin}/socio/${slug}` : null;
  const enabled = hasMicrosite && slug;

  const copyUrl = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  if (!hasMicrosite) {
    return (
      <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="microsite-card-locked">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white">
          <AlertCircle className="h-5 w-5" />
        </span>
        <div>
          <p className="text-sm font-extrabold text-amber-900">Tu micrositio se activa al ser socio</p>
          <p className="mt-1 text-xs text-amber-800">
            Cuando un administrador apruebe tu alta como socio activo, tendrás tu propio micrositio público.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border p-5 ${enabled ? 'border-emerald-200 bg-gradient-to-br from-white to-emerald-50' : 'border-slate-200 bg-white'}`}
      data-testid="microsite-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${enabled ? 'bg-emerald-500' : 'bg-slate-300'} text-white`}>
            <Globe className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
              Mi micrositio público
              {enabled && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" /> Activo
                </span>
              )}
            </p>
            {enabled ? (
              <button
                type="button"
                onClick={copyUrl}
                data-testid="microsite-url-copy"
                className="group mt-1 inline-flex max-w-full items-center gap-1.5 truncate text-xs font-medium text-slate-500 hover:text-cnib-blue"
                title="Copiar enlace"
              >
                <Copy className="h-3 w-3 opacity-60 group-hover:opacity-100" />
                <span className="truncate">{url}</span>
              </button>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                Completa tu perfil (ciudad, WhatsApp, empresa) y guarda los cambios para activar tu micrositio.
              </p>
            )}
          </div>
        </div>
        {enabled && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="microsite-open-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-cnib-navy px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-cnib-blue"
          >
            Ver mi micrositio
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
      {enabled && !isComplete && (
        <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-800">
          Tu micrositio ya está disponible, pero se verá más completo si llenas ciudad, WhatsApp y empresa.
        </p>
      )}
    </div>
  );
}

function SocialInput({ name, icon: Icon, label, value, onChange, placeholder, testid, iconClass }) {
  return (
    <label className="block">
      <span className={`mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500`}>
        <Icon className={`h-3.5 w-3.5 ${iconClass || ''}`} />
        {label}
      </span>
      <input
        type="url"
        name={name}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15"
        data-testid={testid}
      />
    </label>
  );
}
